/**
 * Article structured data (JSON-LD) for blog posts
 * Adds Article schema for richer search results
 */

import { config } from '@/lib/config';
import { getLogoPathForSEO } from '@/lib/constants/logo-paths';
import { JSONLDScript } from './jsonld-script';

interface ArticleStructuredDataProps {
  title: string;
  description: string;
  url: string;
  publishedTime: string;
  modifiedTime?: string;
  author?: {
    name: string;
    url?: string;
    jobTitle?: string;
    sameAs?: string[];
    knowsAbout?: string[];
  };
  image?: string;
  publisher?: {
    name: string;
    logo?: string;
  };
  keywords?: string[];
  articleSection?: string;
  wordCount?: number;
  inLanguage?: string;
}

const DEFAULT_AUTHOR = {
  name: 'Email Ferret Team',
  jobTitle: 'Email Security Experts',
  sameAs: ['https://twitter.com/email_ferret', 'https://www.linkedin.com/company/110188068'],
  knowsAbout: ['email security', 'AI spam detection', 'cold outreach detection', 'Gmail filtering'],
};

/**
 * Bylines that name the company rather than a human.
 *
 * Matched by name so a post that sets a real author in frontmatter still gets
 * Person markup without any extra flag to remember.
 */
const COLLECTIVE_BYLINES = new Set(['email ferret team', 'email ferret']);

export function ArticleStructuredData({
  title,
  description,
  url,
  publishedTime,
  modifiedTime,
  author = DEFAULT_AUTHOR,
  image,
  publisher = { name: 'Email Ferret' },
  keywords,
  articleSection,
  wordCount,
  inLanguage = 'en-US',
}: ArticleStructuredDataProps) {
  const baseUrl = config.appUrl;

  const imageUrl = image || `${baseUrl}/og-image.png`;

  const mergedAuthor = { ...DEFAULT_AUTHOR, ...author };
  const isCollectiveByline = COLLECTIVE_BYLINES.has(mergedAuthor.name.trim().toLowerCase());

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: description,
    image: {
      '@type': 'ImageObject',
      url: imageUrl,
      width: 1200,
      height: 630,
    },
    datePublished: publishedTime,
    dateModified: modifiedTime || publishedTime,
    // A collective byline is an Organization, not a Person. Declaring
    // `Person: "Email Ferret Team"` asserts a human who does not exist, which
    // is both inaccurate and a weak E-E-A-T signal on security content. Named
    // human authors still emit Person, with affiliation back to the org.
    author: isCollectiveByline
      ? {
          '@type': 'Organization',
          name: mergedAuthor.name,
          url: mergedAuthor.url ?? baseUrl,
          ...(mergedAuthor.sameAs &&
            mergedAuthor.sameAs.length > 0 && { sameAs: mergedAuthor.sameAs }),
          ...(mergedAuthor.knowsAbout &&
            mergedAuthor.knowsAbout.length > 0 && { knowsAbout: mergedAuthor.knowsAbout }),
        }
      : {
          '@type': 'Person',
          name: mergedAuthor.name,
          ...(mergedAuthor.url && { url: mergedAuthor.url }),
          ...(mergedAuthor.jobTitle && { jobTitle: mergedAuthor.jobTitle }),
          ...(mergedAuthor.sameAs &&
            mergedAuthor.sameAs.length > 0 && { sameAs: mergedAuthor.sameAs }),
          ...(mergedAuthor.knowsAbout &&
            mergedAuthor.knowsAbout.length > 0 && { knowsAbout: mergedAuthor.knowsAbout }),
          affiliation: {
            '@type': 'Organization',
            name: 'Email Ferret',
            url: baseUrl,
          },
        },
    publisher: {
      '@type': 'Organization',
      name: publisher.name,
      logo: {
        '@type': 'ImageObject',
        url: publisher.logo || `${baseUrl}${getLogoPathForSEO()}`,
        width: 512,
        height: 512,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    url: url,
    inLanguage: inLanguage,
    ...(keywords && keywords.length > 0 && { keywords: keywords.join(', ') }),
    ...(articleSection && { articleSection }),
    ...(wordCount && { wordCount }),
  };

  return <JSONLDScript data={articleSchema} id="article-structured-data" />;
}
